import React, { useState, useEffect } from "react";
import { Navbar, Nav, NavDropdown } from "react-bootstrap";
import stars from "assets/estrellitas.png";
import {
  createAuthenticatedAxiosInstance,
  fetchPublicCategories,
  getFavorites,
} from "api/auth";
import { primaryColorOpacity } from "utils/colors";
import "./ClientMenuBar.scss";
import { PublicCategory } from "interfaces/ICategory";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "../../../zustand/userStore";
import { useAuthStore } from "../../../zustand/loginStore";
import { AxiosInstance } from "axios";
import { useBreakpoints } from "hooks/useBreakPoints";

const ClientMenuBar: React.FC = () => {
  const { favoriteProducts, setFavoriteProducts } = useUserStore();
  const { token } = useAuthStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  const [categories, setCategories] = useState<PublicCategory[]>([]);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);
  const navigate = useNavigate();
  const { isMobile } = useBreakpoints();
  /**
   * Handles navigation to search page with selected category
   * @param {number} categoryId - The ID of the category to filter by
   */
  const handleCategoryClick = (categoryId: number) => {
    navigate(`/search?categories=${categoryId}`);
  };

  /**
   * Handles scroll events in the dropdown menu
   * @function handleScroll
   * @param {React.UIEvent<HTMLElement>} e - Scroll event
   *
   * Calculates if user has scrolled to bottom of dropdown
   * Updates scroll indicator visibility based on position
   */
  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const element = e.currentTarget;
    const isAtBottom =
      Math.abs(
        element.scrollHeight - element.scrollTop - element.clientHeight
      ) < 1;
    setShowScrollIndicator(!isAtBottom);
  };

  /**
   * method that loads favorite products related to a client
   */
  const loadFavorites = async (axiosInstance: AxiosInstance) => {
    const { data, statusCode } = await getFavorites(axiosInstance);
    if (!data) return;
    const favorites = data.filter((product) => product.status);
    setFavoriteProducts(favorites);
  };

  /**
   * Effect hook to fetch and load categories on component mount
   * @effect
   * @dependencies []
   *
   * Makes API call to get public categories
   * Updates categories state with response
   */
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetchPublicCategories();
        setCategories(response);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    loadCategories();
    if (favoriteProducts.length <= 0) {
      loadFavorites(axiosInstance);
    }
  }, []);

  /**
   * Calculates number of visible category links based on screen width
   * @function getVisibleCategories
   * @returns {number} Number of categories to display
   *
   * Returns:
   * - 6 categories for xxl screens (≥1200px)
   * - 5 categories for xl screens (≥1100px)
   * - 4 categories for lg screens (≥1000px)
   * - 3 categories for mid-lg screens (≥900px)
   * - 2 categories for md screens (≥800px)
   * - 1 category for sm-md screens (≥768px)
   * - 0 categories for smaller screens
   */
  const getVisibleCategories = () => {
    const width = window.innerWidth;
    if (width >= 1200) return 5; // xxl screens
    if (width >= 1100) return 4; // xl screens
    if (width >= 1000) return 3; // lg screens
    if (width >= 900) return 2; // mid-lg screens
    if (width >= 800) return 1; // md screens
    if (width >= 768) return 0; // sm-md screens
    return 0; // sm and smaller
  };

  const [visibleCategories, setVisibleCategories] = useState(
    getVisibleCategories()
  );

  /**
   * Effect hook to handle window resize events
   * @effect
   * @dependencies []
   *
   * Updates visible categories count when window is resized
   * Cleans up event listener on unmount
   */
  useEffect(() => {
    const handleResize = () => {
      setVisibleCategories(getVisibleCategories());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Navbar
      bg="white"
      variant="light"
      className="menu-bar"
      style={{
        borderBottom: `1px solid ${primaryColorOpacity}`,
        zIndex: 20,
      }}
    >
      <Nav
        className="mx-auto"
        style={{
          fontSize: visibleCategories < 2 ? "1rem" : "1.1rem",
          fontWeight: "bold",
          color: "#000000",
        }}
      >
        <NavDropdown
          title={<span style={{ color: "#000000" }}>Categorías</span>}
          id="categories-dropdown"
          className={`${
            showScrollIndicator ? "show-indicator" : ""
          } categories-dropdown`}
          onScroll={handleScroll}
        >
          <NavDropdown.Item
            className="d-flex justify-content-between align-items-center"
            onClick={() => navigate("/all-categories")}
          >
            Todas las categorías
            <span style={{ fontSize: "1.5em" }}>›</span>
          </NavDropdown.Item>
          <NavDropdown.Item
            className="d-flex justify-content-between align-items-center"
            onClick={() => navigate("/nuevos")}
          >
            Novedades
            <span style={{ fontSize: "1.5em" }}>›</span>
          </NavDropdown.Item>
          <NavDropdown.Item
            className="d-flex justify-content-between align-items-center"
            onClick={() => navigate("/all-promotions")}
          >
            Ofertas
            <span style={{ fontSize: "1.5em" }}>›</span>
          </NavDropdown.Item>
          <NavDropdown.Divider />
          {categories.map((category) => (
            <NavDropdown.Item
              key={category.id}
              className="d-flex justify-content-between align-items-center"
              onClick={() => handleCategoryClick(category.id)}
            >
              {category.name}
              <span style={{ fontSize: "1.5em" }}>›</span>
            </NavDropdown.Item>
          ))}
        </NavDropdown>
        <Nav.Link
          onClick={() => navigate("/all-promotions")}
          className={visibleCategories < 2 ? "mx-1" : "mx-3"}
          style={{
            color: "#000000",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "200px",
          }}
        >
          Ofertas
        </Nav.Link>
        {categories.slice(0, visibleCategories).map((category) => (
          <Nav.Link
            key={category.id}
            className={visibleCategories < 2 ? "mx-1" : "mx-3"}
            style={{
              color: "#000000",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "200px",
              cursor: "pointer",
            }}
            onClick={() => handleCategoryClick(category.id)}
          >
            {category.name}
          </Nav.Link>
        ))}
        <Nav.Link
          onClick={() => navigate("/nuevos")}
          className={visibleCategories < 2 ? "mx-1" : "mx-3"}
          style={{
            color: "#000000",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "250px",
            cursor: "pointer",
          }}
        >
          {isMobile ? "Nuevos" : "Nuevos Productos"}
          <img
            src={stars}
            alt="estrella"
            className={visibleCategories < 2 ? "ms-1" : "ms-2"}
            style={{
              width: "1.5rem",
              height: "1.5rem",
              verticalAlign: "middle",
            }}
          />
        </Nav.Link>
      </Nav>
    </Navbar>
  );
};

export default ClientMenuBar;
